import React, { useEffect, useMemo, useState } from 'react'
import { Movie, Screening } from '../types'
import { getTimeSeriesBookings, getUpcomingOccupancy, getTopEvents } from '../lib/analytics'

interface OrganizerDashboardProps {
  movies: Movie[]
  validators: Array<{ user_id: string; email: string }>
  registrationsCount: number
  screeningsByMovie: Record<number, Screening[]>
  organizerName?: string
  organizerId?: number | null
  days?: number
  movieId?: number | null
}

export default function OrganizerDashboard({ movies, validators, registrationsCount, screeningsByMovie, organizerName, organizerId, days = 30, movieId = null }: OrganizerDashboardProps) {
  const now = new Date()
  const weekdayLabels = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat']
  const [activityCounts, setActivityCounts] = useState<number[]>(Array(7).fill(0))
  const [activityMax, setActivityMax] = useState(1)
  const [bookingsSeries, setBookingsSeries] = useState<Array<{ date: string; count: number; revenue: number }>>([])
  const [occupancy, setOccupancy] = useState<Array<{ id: number; movie_id: number; start_time: string; capacity: number; booked: number }>>([])
  const [topEvents, setTopEvents] = useState<Array<{ title: string; tickets: number }>>([])
  useEffect(() => {
    const ids = Object.values(screeningsByMovie).flat().map(s => s.id)
    if (!ids.length) { setActivityCounts(Array(7).fill(0)); setActivityMax(1); return }
    (async () => {
      const since = new Date()
      since.setDate(since.getDate() - 6)
      const { data } = await (await import('../lib/supabase')).supabase
        .from('booking_seats')
        .select('created_at, seat_ids, seat_id')
        .in('screening_id', ids)
        .gte('created_at', since.toISOString())
      const counts = Array(7).fill(0)
      for (const r of ((data || []) as any[])) {
        const dt = new Date(r.created_at)
        const add = Array.isArray(r.seat_ids) ? (r.seat_ids as any[]).length : 1
        counts[dt.getDay()] += add
      }
      setActivityCounts(counts)
      setActivityMax(Math.max(1, ...counts))
    })()
  }, [screeningsByMovie])

  useEffect(() => {
    (async () => {
      const s = await getTimeSeriesBookings(days, organizerId || movieId ? { organizerId: organizerId || undefined, movieId: movieId || undefined } : undefined)
      setBookingsSeries(s)
      const oRaw = await getUpcomingOccupancy(6, organizerId || movieId ? { organizerId: organizerId || undefined, movieId: movieId || undefined } : undefined)
      setOccupancy(oRaw.map(r => ({ id: r.id, movie_id: r.movie_id, start_time: r.start_time, capacity: r.capacity, booked: r.booked })))
      const t = await getTopEvents(5, organizerId ? { organizerId } : undefined)
      setTopEvents(t.map(e => ({ title: e.title, tickets: e.tickets })))
    })()
  }, [organizerId, days, movieId])

  const revenueTotal = useMemo(() => bookingsSeries.reduce((sum, r) => sum + (r.revenue || 0), 0), [bookingsSeries])

  const statusCounts = (() => {
    let active = 0, inactive = 0, completed = 0, cancelled = 0
    const activeTitles: string[] = []
    const inactiveTitles: string[] = []
    const completedTitles: string[] = []
    const cancelledTitles: string[] = []
    for (const m of movies) {
      const mAny = m as any
      const scs = screeningsByMovie[m.id] || []
      const hasUpcoming = scs.some(s => new Date(s.start_time) >= now)
      const allPast = scs.length > 0 && scs.every(s => new Date(s.start_time) < now)
      const startOk = !mAny.booking_start_time || (new Date(mAny.booking_start_time) <= now)
      const stopOk = !mAny.booking_stop_enabled || (mAny.booking_stop_time && new Date(mAny.booking_stop_time) >= now)
      const bookingOnNow = !!mAny.accept_booking && startOk && stopOk
      if (mAny.is_canceled) {
        cancelled++
        cancelledTitles.push(m.title)
      } else if (allPast) {
        completed++
        completedTitles.push(m.title)
      } else if (hasUpcoming && bookingOnNow) {
        active++
        activeTitles.push(m.title)
      } else {
        inactive++
        inactiveTitles.push(m.title)
      }
    }
    const total = active + inactive + completed + cancelled || 1
    return { active, inactive, completed, cancelled, total, activeTitles, inactiveTitles, completedTitles, cancelledTitles }
  })()
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="flex flex-col gap-2 rounded-xl p-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
          <p className="text-gray-700 dark:text-gray-300 text-base font-medium">Total Events</p>
          <p className="text-gray-900 dark:text-white text-3xl font-bold">{movies.length}</p>
        </div>
        <div className="flex flex-col gap-2 rounded-xl p-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
          <p className="text-gray-700 dark:text-gray-300 text-base font-medium">Total Validators</p>
          <p className="text-gray-900 dark:text-white text-3xl font-bold">{validators.length}</p>
        </div>
        <div className="flex flex-col gap-2 rounded-xl p-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
          <p className="text-gray-700 dark:text-gray-300 text-base font-medium">Registrations</p>
          <p className="text-gray-900 dark:text-white text-3xl font-bold">{registrationsCount}</p>
        </div>
        <div className="flex flex-col gap-2 rounded-xl p-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
          <p className="text-gray-700 dark:text-gray-300 text-base font-medium">Screenings</p>
          <p className="text-gray-900 dark:text-white text-3xl font-bold">{Object.values(screeningsByMovie).reduce((acc, arr) => acc + (arr?.length || 0), 0)}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="flex flex-col gap-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6">
          <div className="flex items-center justify-between">
            <p className="text-gray-700 dark:text-gray-300 text-base font-medium">Bookings ({days}d)</p>
          </div>
          <div className="mt-2">
            {bookingsSeries.length ? (
              <svg width={240} height={60} className="overflow-visible">
                {(() => {
                  const values = bookingsSeries.map(b => b.count)
                  const max = Math.max(...values)
                  const min = Math.min(...values)
                  const span = Math.max(1, max - min)
                  const stepX = 240 / Math.max(1, values.length - 1)
                  const pts = values.map((v, i) => ({ x: i * stepX, y: 60 - ((v - min) / span) * 60 }))
                  const d = pts.map((p, i) => (i === 0 ? `M ${p.x} ${p.y}` : `L ${p.x} ${p.y}`)).join(' ')
                  const area = `M 0 60 ` + pts.map((p) => `L ${p.x} ${p.y}`).join(' ') + ` L 240 60 Z`
                  return (
                    <>
                      <path d={area} fill="#4f46e522" />
                      <path d={d} fill="none" stroke="#4f46e5" strokeWidth={2} />
                    </>
                  )
                })()}
              </svg>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">No recent bookings</p>
            )}
          </div>
        </div>
        {bookingsSeries.some(b => b.revenue > 0) && (
          <div className="flex flex-col gap-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6">
            <div className="flex items-center justify-between">
              <p className="text-gray-700 dark:text-gray-300 text-base font-medium">Revenue ({days}d)</p>
              <p className="text-gray-900 dark:text-white text-sm font-semibold">€{revenueTotal.toFixed(2)}</p>
            </div>
            <div className="mt-2">
              {(() => {
                const values = bookingsSeries.map(b => b.revenue || 0)
                if (!values.some(v => v > 0)) return <p className="text-sm text-gray-500 dark:text-gray-400">No revenue data</p>
                const max = Math.max(...values)
                const min = Math.min(...values)
                const span = Math.max(1, max - min)
                const stepX = 240 / Math.max(1, values.length - 1)
                const pts = values.map((v, i) => ({ x: i * stepX, y: 60 - ((v - min) / span) * 60 }))
                const d = pts.map((p, i) => (i === 0 ? `M ${p.x} ${p.y}` : `L ${p.x} ${p.y}`)).join(' ')
                const area = `M 0 60 ` + pts.map((p) => `L ${p.x} ${p.y}`).join(' ') + ` L 240 60 Z`
                return (
                  <svg width={240} height={60} className="overflow-visible">
                    <path d={area} fill="#f59e0b22" />
                    <path d={d} fill="none" stroke="#f59e0b" strokeWidth={2} />
                  </svg>
                )
              })()}
            </div>
          </div>
        )}
        <div className="flex flex-col gap-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6">
          <div className="flex flex-col">
            <p className="text-gray-700 dark:text-gray-300 text-base font-medium">Event Status</p>
            <p className="text-gray-900 dark:text-white text-3xl font-bold">{movies.length} Events</p>
            <p className="text-gray-500 dark:text-gray-400 text-sm">All Time</p>
          </div>
          <div className="grid min-h-[180px] grid-flow-col gap-4 grid-rows-[1fr_auto] items-end justify-items-center">
            <div className="bg-green-500 w-full rounded-t" title={(statusCounts.activeTitles.length ? statusCounts.activeTitles.join(', ') : 'None')} style={{ height: `${Math.round((statusCounts.active / statusCounts.total) * 100)}%` }}></div>
            <p className="text-gray-400 text-xs">Active</p>
            <div className="bg-slate-500 w-full rounded-t" title={(statusCounts.inactiveTitles.length ? statusCounts.inactiveTitles.join(', ') : 'None')} style={{ height: `${Math.round((statusCounts.inactive / statusCounts.total) * 100)}%` }}></div>
            <p className="text-gray-400 text-xs">Inactive</p>
            <div className="bg-indigo-600 w-full rounded-t" title={(statusCounts.completedTitles.length ? statusCounts.completedTitles.join(', ') : 'None')} style={{ height: `${Math.round((statusCounts.completed / statusCounts.total) * 100)}%` }}></div>
            <p className="text-gray-400 text-xs">Completed</p>
            <div className="bg-red-500 w-full rounded-t" title={(statusCounts.cancelledTitles.length ? statusCounts.cancelledTitles.join(', ') : 'None')} style={{ height: `${Math.round((statusCounts.cancelled / statusCounts.total) * 100)}%` }}></div>
            <p className="text-gray-400 text-xs">Cancelled</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="flex flex-col gap-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6">
          <h3 className="text-gray-900 dark:text-white text-lg font-semibold">Upcoming Screenings</h3>
          <div className="mt-2 space-y-3">
            {occupancy.length === 0 ? (
              <p className="text-sm text-gray-700 dark:text-gray-300">No upcoming screenings</p>
            ) : occupancy.map(s => {
              const pct = s.capacity > 0 ? Math.round((s.booked / s.capacity) * 100) : 0
              const title = (movies.find(m => m.id === s.movie_id)?.title) || ''
              return (
                <div key={s.id} className="flex items-center gap-3">
                  <div className="flex-1 h-2 rounded bg-gray-200 dark:bg-gray-700">
                    <div className={`h-2 rounded ${pct >= 90 ? 'bg-red-500' : pct >= 60 ? 'bg-yellow-500' : 'bg-green-500'}`} style={{ width: `${Math.min(100, pct)}%` }} />
                  </div>
                  <div className="min-w-[240px] text-sm text-gray-900 dark:text-white">{new Date(s.start_time).toLocaleString()} {title && `- ${title}`}</div>
                  <div className="w-20 text-right text-sm text-gray-700 dark:text-gray-300">{pct}%</div>
                </div>
              )
            })}
          </div>
        </div>
        <div className="flex flex-col gap-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6">
          <h3 className="text-gray-900 dark:text-white text-lg font-semibold">Top Events</h3>
          <div className="mt-2 space-y-2">
            {topEvents.length ? (
              topEvents.map(e => (
                <div key={e.title} className="flex items-center gap-3">
                  <div className="flex-1 h-2 rounded bg-gray-200 dark:bg-gray-700">
                    <div className="h-2 rounded bg-indigo-600 dark:bg-indigo-400" style={{ width: `${Math.min(100, e.tickets)}%` }} />
                  </div>
                  <div className="min-w-[140px] text-sm text-gray-900 dark:text-white">{e.title}</div>
                  <div className="w-12 text-right text-sm text-gray-700 dark:text-gray-300">{e.tickets}</div>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">No ranking available</p>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
